import { getErrors } from "hooks/useErrors"
import produce from "immer"
import { cloneDeep, get, set as setObj, update } from "lodash-es"
import DCRegistrationFormModel from "models/distribution-center.model"
import { mountStoreDevtool } from "simple-zustand-devtools"
import differenceMap from "utils/difference-map"
import { InferType } from "yup"
import create from "zustand"

export type DCRegistrationFormModelType = InferType<
  typeof DCRegistrationFormModel
>
export type DCStateType = {
  state: DCRegistrationFormModelType
  lastUpdated: DCRegistrationFormModelType
  changed: any
  disabled: boolean
  errors?: any
}
export type DCStoreType = DCStateType & {
  initializeState: (state: DCStateType) => void
  setData: (
    key: keyof DCRegistrationFormModelType
  ) => (path: string, value: any) => void
  removeListItem: (
    key: string,
    index: number,
    updateLastSaved?: boolean
  ) => void
  addListItem: (key: string, item: any) => void
  setPinAutoComplete: (data: any) => void
  setBankAutocomplete: (bankIndex: number, data: any, serverBank: any) => void
  reset: () => void
}

const INITIAL_DATA: DCRegistrationFormModelType = {
  basicDetails: {
    geometry: {
      coordinates: [],
    },
  } as any,

  billingDetails: {
    businessPlace: "",
    profitCenter: "",
    costCenter: "",
    applicable: "no",
    gstin: "",
    registeredName: "",
    address1: "",
    address2: "",
    state: {},
    district: "",
    pincode: "",
  },
  deliveryCharges: {
    isManufacturingPlant: false,
    deliveryCosts: [{} as any],
    minCartValue: 0,
    estimatedDelta: "",
    extraSapCodes: {},
  },
  surveyAreas: [],
  paymentMethods: [{} as any],
  licenseDetails: [{} as any],
  operationalSlots: {
    deliverySlots: [],
    maintenanceSlots: [],
    pickupSlots: [],
    alternateSalesSlots: [],
  },
  stockPreferences: {
    currentStockPercentage: null,
  },
}

const useDCStore = create<DCStoreType>(set => ({
  disabled: false,
  state: INITIAL_DATA,
  lastUpdated: {},
  changed: INITIAL_DATA,
  initializeState: set,
  errors: {},
  setData(key) {
    return (path: string, value: any) => {
      set(
        produce((store: DCStoreType) => {
          setObj(store.state[key], path, value)
          setObj(
            store.errors,
            key,
            getErrors({
              schema: DCRegistrationFormModel.fields[key],
              state: store.state[key],
            })
          )
          if (store.state.basicDetails.id) {
            // If id is present, then changed is the delta between lastUpdated and current state
            setObj(
              store.changed,
              key,
              differenceMap(
                cloneDeep(store.lastUpdated[key]),
                cloneDeep(store.state[key]),
                {
                  leaveUnchanged: [
                    "slots",
                    "coordinates",
                    "areaCatered",
                    "deliveryCosts",
                    "groupVisibility",
                    "pointOfContacts",
                    "photos",
                    "attachments",
                    "users",
                    "allowedDepartments",
                  ],
                }
              )
            )

            if (key.includes("geometry.coordinates")) {
              setObj(
                store.changed.basicDetails,
                "geometry",
                store.state.basicDetails.geometry
              )
            }
          } else {
            // If id is not present then changed is the SAME as current state
            store.changed[key] = cloneDeep(store.state[key])
          }
        })
      )
    }
  },
  setPinAutoComplete(data: any) {
    set(
      produce((store: DCStoreType) => {
        if (data.state) {
          store.state.billingDetails.state = {
            // Replace it with value once we get the data from server
            id: data.state_code,
            name: data.state,
            codename: data.state_code,
          }
        }

        if (data.district) {
          store.state.billingDetails.district = data.district
        }

        if (data.pincode) {
          store.state.billingDetails.pincode = data.pincode
        }

        setObj(
          store.errors,
          `billingDetails`,
          getErrors({
            schema: DCRegistrationFormModel.fields.billingDetails,
            state: store.state.billingDetails,
          })
        )
      })
    )
  },

  setBankAutocomplete(bankIndex, data, serverBank) {
    set(
      produce((store: DCStoreType) => {
        store.state.paymentMethods[bankIndex].ifscCode = data.IFSC
        store.state.paymentMethods[bankIndex].bank = serverBank
        store.state.paymentMethods[bankIndex].branchName = data.BRANCH

        setObj(
          store.errors,
          `paymentMethods`,
          getErrors({
            schema: DCRegistrationFormModel.fields.paymentMethods,
            state: store.state.paymentMethods,
          })
        )

        setObj(
          store.changed,
          "paymentMethods",
          differenceMap(
            cloneDeep(store.lastUpdated["paymentMethods"]),
            cloneDeep(store.state["paymentMethods"])
          )
        )
      })
    )
  },
  removeListItem(key, index, updateLastSaved) {
    set(
      produce((store: DCStoreType) => {
        update(store.state, key, val =>
          val.filter((_: any, i: number) => i !== index)
        )
        if (updateLastSaved) {
          update(store.lastUpdated, key, val =>
            val.filter((_: any, i: number) => i !== index)
          )
        }

        setObj(
          store.changed,
          key,
          differenceMap(
            cloneDeep(get(store.lastUpdated, key)),
            cloneDeep(get(store.state, key)),
            {
              leaveUnchanged: [
                "slots",
                "coordinates",
                "areaCatered",
                "deliveryCosts",
                "groupVisibility",
                "pointOfContacts",
                "photos",
                "attachments",
                "users",
                "allowedDepartments",
              ],
            }
          )
        )
        if (key.includes("geometry.coordinates")) {
          setObj(
            store.changed.basicDetails,
            "geometry",
            store.state.basicDetails.geometry
          )
        }
      })
    )
  },
  addListItem(key: string, item: any) {
    set(
      produce((store: DCStoreType) => {
        update(store.state, key, val => (val?.length ? val : []).concat(item))
        setObj(
          store.changed,
          key,
          differenceMap(
            cloneDeep(get(store.lastUpdated, key)),
            cloneDeep(get(store.state, key)),
            {
              leaveUnchanged: [
                "slots",
                "coordinates",
                "areaCatered",
                "deliveryCosts",
                "groupVisibility",
                "pointOfContacts",
                "photos",
                "attachments",
                "users",
                "allowedDepartments",
              ],
            }
          )
        )
        if (key.includes("geometry.coordinates")) {
          setObj(
            store.changed.basicDetails,
            "geometry",
            store.state.basicDetails.geometry
          )
        }
      })
    )
  },
  reset() {
    set(
      produce((store: DCStoreType) => {
        store.state = INITIAL_DATA
        store.changed = {}
        store.lastUpdated = INITIAL_DATA
        store.disabled = false
        store.errors = {}
      })
    )
  },
}))
if (process.env.NODE_ENV === "development") {
  mountStoreDevtool("DCFormStore", useDCStore)
}
export default useDCStore
